import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  FaFileContract,
  FaSearch,
  FaEdit,
  FaHistory,
  FaChevronDown,
  FaChevronUp,
  FaChevronRight,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaCircle,
  FaSync,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountUp,
  FaPlus,
  FaTrash,
  FaTimes,
  FaSave,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaLayerGroup,
  FaWifi,
  FaNetworkWired,
  FaBolt,
  FaBuilding,
  FaServer,
  FaShieldAlt,
  FaCloud,
  FaPhone,
  FaTools,
  FaLaptop,
  FaUpload,
  FaFileAlt,
  FaDownload,
  FaPaperclip,
  FaFilePdf
} from 'react-icons/fa';
import { useAuth } from '@/context/AuthContext';
import './ServiceContractsPage.scss';
import * as Sentry from '@sentry/react';

// ────────────────────────────────────────────────────────────────────────────────
// Icon mapping — converts FontAwesome CSS class names stored in the database to
// their equivalent React Icon components so that no raw FA CSS class strings are
// rendered directly via <i> tags.  Falls back to a generic icon when the class
// is not in the map.
// ────────────────────────────────────────────────────────────────────────────────
const FA_ICON_MAP = {
  'fa-wifi':           FaWifi,
  'fa-network-wired':  FaNetworkWired,
  'fa-bolt':           FaBolt,
  'fa-building':       FaBuilding,
  'fa-server':         FaServer,
  'fa-shield-alt':     FaShieldAlt,
  'fa-cloud':          FaCloud,
  'fa-phone':          FaPhone,
  'fa-tools':          FaTools,
  'fa-laptop':         FaLaptop,
  'fa-file-contract':  FaFileContract,
  'fa-layer-group':    FaLayerGroup,
  'fa-money-bill-wave': FaMoneyBillWave,
};

// ────────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────────

function getContractStatus(earliestExpiry) {
  if (!earliestExpiry) return 'unknown';
  const now = new Date();
  const expiry = new Date(earliestExpiry);
  const diffDays = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'expired';
  if (diffDays <= 30) return 'soon';
  return 'active';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric'
  });
}

function formatCurrency(val) {
  if (val == null || val === 0) return '—';
  return `R${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ────────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = {
    active:  { cls: 'status-active',  icon: <FaCheckCircle />,       label: 'Active' },
    expired: { cls: 'status-expired', icon: <FaTimesCircle />,       label: 'Expired' },
    soon:    { cls: 'status-soon',    icon: <FaExclamationTriangle />, label: 'Expiring Soon' },
    unknown: { cls: 'status-unknown', icon: <FaCircle />,             label: 'No Expiry Set' }
  }[status] ?? { cls: 'status-unknown', icon: <FaCircle />, label: 'Unknown' };

  return (
    <span className={`scp-status-badge ${cfg.cls}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function ExpiryCell({ expiryDate }) {
  if (!expiryDate) return <span className="scp-expiry-none">—</span>;
  const status = getContractStatus(expiryDate);
  const cls = {
    active:  'scp-expiry-ok',
    expired: 'scp-expiry-expired',
    soon:    'scp-expiry-soon',
    unknown: 'scp-expiry-none'
  }[status];
  return <span className={cls}>{formatDate(expiryDate)}</span>;
}

function ContractTypeBadge({ name, iconClass, color }) {
  // Resolve the React Icon component for the stored FA class string, falling back
  // to a generic icon if the class isn't in the map (avoids raw <i> FA tags).
  const IconComponent = (iconClass && FA_ICON_MAP[iconClass]) || (iconClass ? FaLayerGroup : null);

  return (
    <span className="scp-type-badge" style={{ borderColor: color || '#ddd', color: color || '#555' }}>
      {IconComponent && <IconComponent style={{ fontSize: '0.7rem', marginRight: '3px', verticalAlign: 'middle' }} />}
      {name || 'Unknown Type'}
    </span>
  );
}

function HistoryPanel({ contractId, apiFetch }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await apiFetch(`/api/ServiceContracts/${contractId}/history`);
        if (!res.ok) throw new Error('Failed to load history');
        const json = await res.json();
        if (!cancelled) setHistory(json.data || []);
      } catch (catchError) {
          Sentry.captureException(catchError);
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [contractId, apiFetch]);

  if (loading) {
    return (
      <div className="scp-history-panel">
        <div className="scp-history-title">Change History</div>
        <div className="scp-history-loading">Loading history…</div>
      </div>
    );
  }

  return (
    <div className="scp-history-panel">
      <div className="scp-history-title">Change History</div>
      {history.length === 0 ? (
        <div className="scp-history-empty">No changes recorded yet.</div>
      ) : (
        <ul className="scp-history-list">
          {history.map((entry, idx) => (
            <li key={idx} className="scp-history-item">
              <div className="scp-hist-dot" />
              <div className="scp-hist-body">
                <div className="scp-hist-timestamp">
                  {entry.changedAt ? new Date(entry.changedAt).toLocaleString() : '—'}
                  {entry.changedByUserName && <> · {entry.changedByUserName}</>}
                </div>
                <div className="scp-hist-changes">
                  <span className="scp-hist-field">{entry.changeType}</span>
                  {entry.fieldsChanged && (
                    <span style={{ marginLeft: 6, color: '#777', fontSize: '0.8rem' }}>
                      {entry.fieldsChanged}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────────
// Document upload / list sub-component
// ────────────────────────────────────────────────────────────────────────────────

function DocumentList({ documents, apiFetch, onDeleted }) {
  if (!documents || documents.length === 0) return null;

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const res = await apiFetch(`/api/ServiceContracts/documents/${docId}`, { method: 'DELETE' });

      // Check res.ok before parsing to avoid opaque JSON parse errors on non-2xx responses.
      if (!res.ok) {
        let errorMessage = `Delete failed (HTTP ${res.status})`;
        try {
          const errBody = await res.json();
          if (errBody?.error) errorMessage = errBody.error;
        } catch (catchError) {
            Sentry.captureException(catchError);
          // Response is not JSON — keep the HTTP-status based fallback message.
        }
        throw new Error(errorMessage);
      }

      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      toast.success('Document deleted.');
      onDeleted?.();
    } catch (err) {
        Sentry.captureException(err);
      toast.error(err.message || 'Failed to delete document.');
    }
  };

  return (
    <div className="scp-doc-list">
      {documents.map(doc => (
        <div key={doc.serviceProviderDocumentId} className="scp-doc-item">
          <span className="scp-doc-icon">
            {doc.isPdf ? <FaFilePdf style={{ color: '#e74c3c' }} /> : <FaFileAlt style={{ color: '#3498db' }} />}
          </span>
          <div className="scp-doc-info">
            <a href={doc.downloadUrl} target="_blank" rel="noopener noreferrer" className="scp-doc-name">
              {doc.fileName}
            </a>
            <span className="scp-doc-meta">
              {doc.fileSizeFormatted}
              {doc.description && <> · {doc.description}</>}
            </span>
          </div>
          <div className="scp-doc-actions">
            <a href={doc.downloadUrl} className="scp-doc-download" title="Download">
              <FaDownload />
            </a>
            {onDeleted && (
              <button className="scp-doc-delete" onClick={() => handleDelete(doc.serviceProviderDocumentId)} title="Delete">
                <FaTrash />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function DocumentUploadArea({ uploadUrl, apiFetch, onUploaded }) {
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use apiFetch for centralized auth token management.
      // Note: Content-Type is auto-set by the browser for FormData (includes boundary).
      const res = await apiFetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      // Check res.ok before parsing to avoid opaque JSON parse errors on non-2xx responses.
      if (!res.ok) {
        let errorMessage = `Upload failed (HTTP ${res.status})`;
        try {
          const errBody = await res.json();
          if (errBody?.error) errorMessage = errBody.error;
        } catch (catchError) {
            Sentry.captureException(catchError);
          // Body is not JSON — use the HTTP-status based message.
        }
        throw new Error(errorMessage);
      }

      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Upload failed');
      toast.success('File uploaded successfully.');
      onUploaded?.();
    } catch (err) {
        Sentry.captureException(err);
      toast.error(err.message || 'Failed to upload file.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <label className="scp-upload-area">
      <input type="file" onChange={handleFileSelect} disabled={uploading} style={{ display: 'none' }} />
      <FaUpload style={{ marginRight: 6 }} />
      {uploading ? 'Uploading…' : 'Upload Document'}
    </label>
  );
}

// ────────────────────────────────────────────────────────────────────────────────
// Terms sub-row
// ────────────────────────────────────────────────────────────────────────────────

function TermsPanel({ terms, customFieldsSchema }) {
  if (!terms || terms.length === 0) {
    return <div className="scp-terms-empty">No terms defined for this contract.</div>;
  }

  // Parse custom field schema for labelling
  let fieldDefs = [];
  if (customFieldsSchema) {
    try { fieldDefs = JSON.parse(customFieldsSchema); }
    catch (catchError) {
        Sentry.captureException(catchError);
        /* ignore */
    }
  }

  return (
    <div className="scp-terms-panel">
      <table className="scp-terms-table">
        <thead>
          <tr>
            <th>Term Name</th>
            <th>Start Date</th>
            <th>Expiry Date</th>
            <th>Status</th>
            <th>Monthly Cost</th>
            <th>Linked To</th>
            {fieldDefs.map(f => <th key={f.key}>{f.label}</th>)}
            <th>Documents</th>
          </tr>
        </thead>
        <tbody>
          {terms.map(term => {
            const status = getContractStatus(term.expiryDate);
            let customValues = {};
            if (term.customData) {
              try { customValues = JSON.parse(term.customData); }
              catch (catchError) {
                  Sentry.captureException(catchError);
                  /* ignore */
              }
            }
            const termDocs = term.documents || [];
            return (
              <tr key={term.serviceContractTermId}>
                <td>
                  <div style={{ fontWeight: 600, color: '#222' }}>{term.name}</div>
                  {term.description && (
                    <div style={{ fontSize: '0.75rem', color: '#888' }}>{term.description}</div>
                  )}
                </td>
                <td>{formatDate(term.startDate)}</td>
                <td><ExpiryCell expiryDate={term.expiryDate} /></td>
                <td><StatusBadge status={status} /></td>
                <td>{formatCurrency(term.monthlyCost)}</td>
                <td style={{ fontSize: '0.8rem', color: '#777' }}>
                  {term.linkedEntityType
                    ? `${term.linkedEntityType} #${term.linkedEntityId}`
                    : '—'}
                </td>
                {fieldDefs.map(f => (
                  <td key={f.key} style={{ fontSize: '0.85rem' }}>
                    {customValues[f.key] != null
                      ? `${customValues[f.key]}${f.unit ? ` ${f.unit}` : ''}`
                      : '—'}
                  </td>
                ))}
                <td>
                  {termDocs.length > 0 ? (
                    <div className="scp-term-docs-inline">
                      {termDocs.map(d => (
                        <a key={d.serviceProviderDocumentId} href={d.downloadUrl}
                          target="_blank" rel="noopener noreferrer"
                          className="scp-term-doc-link" title={d.fileName}>
                          {d.isPdf ? <FaFilePdf style={{ color: '#e74c3c' }} /> : <FaFileAlt style={{ color: '#3498db' }} />}
                          <span>{d.fileName}</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: '#ccc', fontSize: '0.8rem' }}>—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────────
// Contract Edit Modal
// ────────────────────────────────────────────────────────────────────────────────

function ContractEditModal({ contract, contractTypes, providers, departments, apiFetch, onClose, onSaved }) {
  const isNew = !contract;
  const [form, setForm] = useState({
    serviceProviderId: contract?.serviceProviderId || '',
    serviceContractTypeId: contract?.serviceContractTypeId || '',
    contractReference: contract?.contractReference || '',
    linkedEntityType: contract?.linkedEntityType || '',
    linkedEntityId: contract?.linkedEntityId || '',
    responsibleDepartmentId: contract?.responsibleDepartmentId || '',
    notes: contract?.notes || '',
  });
  const [terms, setTerms] = useState(contract?.terms || []);
  const [contractDocs, setContractDocs] = useState(contract?.documents || []);
  const [saving, setSaving] = useState(false);

  const selectedType = contractTypes.find(t => t.serviceContractTypeId === Number(form.serviceContractTypeId));
  let customFields = [];
  if (selectedType?.customFieldsSchema) {
    try { customFields = JSON.parse(selectedType.customFieldsSchema); }
    catch (catchError) {
        Sentry.captureException(catchError);
        /* ignore */
    }
  }

  // Parse linked entity types from the selected contract type
  let linkedEntityOptions = [];
  if (selectedType?.linkedEntityTypes) {
    try { linkedEntityOptions = JSON.parse(selectedType.linkedEntityTypes); }
    catch (catchError) {
        Sentry.captureException(catchError);
        /* ignore */
    }
  }

  // When the contract type changes, clear the linked entity if it's no longer valid
  const handleTypeChange = (newTypeId) => {
    setForm(prev => {
      const newType = contractTypes.find(t => t.serviceContractTypeId === Number(newTypeId));
      let newEntityTypes = [];
      if (newType?.linkedEntityTypes) {
        try { newEntityTypes = JSON.parse(newType.linkedEntityTypes); }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* ignore */
        }
      }
      const entityStillValid = newEntityTypes.includes(prev.linkedEntityType);
      return {
        ...prev,
        serviceContractTypeId: newTypeId,
        linkedEntityType: entityStillValid ? prev.linkedEntityType : '',
        linkedEntityId: entityStillValid ? prev.linkedEntityId : ''
      };
    });
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleTermChange = (idx, field, value) => {
    setTerms(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const handleTermCustomDataChange = (idx, key, value) => {
    setTerms(prev => prev.map((t, i) => {
      if (i !== idx) return t;
      let cd = {};
      if (t.customData) {
        try { cd = JSON.parse(t.customData); }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* ignore */
        }
      }
      cd[key] = value;
      return { ...t, customData: JSON.stringify(cd) };
    }));
  };

  const addTerm = () => {
    setTerms(prev => [...prev, {
      name: '', description: '', startDate: '', expiryDate: '',
      expiryReminderDays: '', monthlyCost: '', customData: null,
      linkedEntityType: '', linkedEntityId: '', notes: '', documents: []
    }]);
  };

  // Reload full contract data after doc upload/delete
  const reloadContractData = async () => {
    if (!contract?.serviceContractId) return;
    try {
      const res = await apiFetch(`/api/ServiceContracts/${contract.serviceContractId}`);
      const json = await res.json();
      if (json.success && json.data) {
        setContractDocs(json.data.documents || []);
        setTerms(prev => prev.map(t => {
          if (!t.serviceContractTermId) return t;
          const fresh = (json.data.terms || []).find(ft => ft.serviceContractTermId === t.serviceContractTermId);
          return fresh ? { ...t, documents: fresh.documents || [] } : t;
        }));
      }
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        /* best effort */
    }
  };

  const removeTerm = (idx) => {
    setTerms(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!form.serviceProviderId) { toast.error('Please select a service provider.'); return; }
    if (!form.serviceContractTypeId) { toast.error('Please select a contract type.'); return; }
    setSaving(true);

    try {
      // Build the term payload, preserving the existing term ID so the update
      // path can distinguish existing terms (PUT) from new ones (POST).
      const termPayload = terms.map(t => ({
        serviceContractTermId: t.serviceContractTermId ?? null,
        name: t.name || 'Unnamed Term',
        description: t.description || null,
        linkedEntityType: t.linkedEntityType || null,
        linkedEntityId: t.linkedEntityId ? Number(t.linkedEntityId) : null,
        startDate: t.startDate || null,
        expiryDate: t.expiryDate || null,
        expiryReminderDays: t.expiryReminderDays ? Number(t.expiryReminderDays) : null,
        monthlyCost: t.monthlyCost ? Number(t.monthlyCost) : null,
        customData: t.customData || null,
        notes: t.notes || null,
      }));

      if (isNew) {
        const res = await apiFetch('/api/ServiceContracts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            serviceProviderId: Number(form.serviceProviderId),
            serviceContractTypeId: Number(form.serviceContractTypeId),
            contractReference: form.contractReference || null,
            linkedEntityType: form.linkedEntityType || null,
            linkedEntityId: form.linkedEntityId ? Number(form.linkedEntityId) : null,
            responsibleDepartmentId: form.responsibleDepartmentId ? Number(form.responsibleDepartmentId) : null,
            notes: form.notes || null,
            terms: termPayload
          })
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'Failed to create contract');
        toast.success('Contract created successfully.');
      } else {
        // Update contract header
        const res = await apiFetch(`/api/ServiceContracts/${contract.serviceContractId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            serviceProviderId: Number(form.serviceProviderId),
            serviceContractTypeId: Number(form.serviceContractTypeId),
            contractReference: form.contractReference || null,
            linkedEntityType: form.linkedEntityType || null,
            linkedEntityId: form.linkedEntityId ? Number(form.linkedEntityId) : null,
            responsibleDepartmentId: form.responsibleDepartmentId ? Number(form.responsibleDepartmentId) : null,
            notes: form.notes || null,
            isActive: true
          })
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'Failed to update contract');

        // ── Determine which terms were removed since the modal was opened ──────────
        // Compare the original term IDs (from the contract prop) against the IDs
        // still present in the current terms state.  Any IDs that are missing have
        // been removed by the user and need to be soft-deleted on the server.
        const originalTermIds = (contract?.terms || [])
          .map(t => t.serviceContractTermId)
          .filter(Boolean);

        const currentTermIds = termPayload
          .map(t => t.serviceContractTermId)
          .filter(Boolean);

        const removedTermIds = originalTermIds.filter(id => !currentTermIds.includes(id));

        // Delete removed terms
        for (const termId of removedTermIds) {
          await apiFetch(`/api/ServiceContracts/${contract.serviceContractId}/terms/${termId}`, {
            method: 'DELETE'
          });
        }

        // ── Sync remaining terms: PUT for existing, POST for new ─────────────────
        for (const t of termPayload) {
          if (t.serviceContractTermId) {
            // Existing term — update it via PUT
            await apiFetch(`/api/ServiceContracts/${contract.serviceContractId}/terms/${t.serviceContractTermId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(t)
            });
          } else {
            // New term — create it via POST
            await apiFetch(`/api/ServiceContracts/${contract.serviceContractId}/terms`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(t)
            });
          }
        }

        toast.success('Contract updated successfully.');
      }

      onSaved();
    } catch (err) {
        Sentry.captureException(err);
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="scp-modal-overlay" onClick={onClose}>
      <div className="scp-modal" onClick={e => e.stopPropagation()}>
        <div className="scp-modal-header">
          <h2>{isNew ? 'New Service Contract' : 'Edit Service Contract'}</h2>
          <button className="scp-modal-close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="scp-modal-body">
          {/* Contract Header Fields */}
          <div className="scp-form-grid">
            <div className="scp-form-group">
              <label>Service Provider *</label>
              <select value={form.serviceProviderId} onChange={e => handleChange('serviceProviderId', e.target.value)}>
                <option value="">— Select Provider —</option>
                {providers.map(p => (
                  <option key={p.serviceProviderId} value={p.serviceProviderId}>
                    {p.name} ({p.serviceCategory})
                  </option>
                ))}
              </select>
              <small className="scp-field-hint">The vendor or company providing this service. Providers are managed under Service Provider Registry.</small>
            </div>
            <div className="scp-form-group">
              <label>Contract Type *</label>
              <select value={form.serviceContractTypeId} onChange={e => handleTypeChange(e.target.value)}>
                <option value="">— Select Type —</option>
                {contractTypes.filter(t => t.isActive).map(t => (
                  <option key={t.serviceContractTypeId} value={t.serviceContractTypeId}>
                    {t.name} ({t.serviceCategory}{t.subCategory ? ` / ${t.subCategory}` : ''})
                  </option>
                ))}
              </select>
              <small className="scp-field-hint">Determines which fields and linked entities are available for this contract.</small>
            </div>
            <div className="scp-form-group">
              <label>Contract Reference</label>
              <input type="text" value={form.contractReference} onChange={e => handleChange('contractReference', e.target.value)} placeholder="e.g. Account # or contract ref" />
              <small className="scp-field-hint">Your internal or the provider&apos;s contract/account number for easy lookup.</small>
            </div>
            <div className="scp-form-group">
              <label>Responsible Department</label>
              <select value={form.responsibleDepartmentId} onChange={e => handleChange('responsibleDepartmentId', e.target.value)}>
                <option value="">— Use Type Default —</option>
                {departments.map(d => (
                  <option key={d.departmentID} value={d.departmentID}>{d.departmentName}</option>
                ))}
              </select>
              <small className="scp-field-hint">The department responsible for managing this contract. Leave blank to use the default for the selected contract type.</small>
            </div>
            {linkedEntityOptions.length > 0 && (
              <>
                <div className="scp-form-group">
                  <label>Linked Entity Type</label>
                  <select value={form.linkedEntityType} onChange={e => {
                    handleChange('linkedEntityType', e.target.value);
                    if (!e.target.value) handleChange('linkedEntityId', '');
                  }}>
                    <option value="">— None —</option>
                    {linkedEntityOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                {form.linkedEntityType && (
                  <div className="scp-form-group">
                    <label>{form.linkedEntityType} ID</label>
                    <input type="number" value={form.linkedEntityId} onChange={e => handleChange('linkedEntityId', e.target.value)} placeholder={`Enter ${form.linkedEntityType} ID`} />
                  </div>
                )}
              </>
            )}
            <div className="scp-form-group scp-form-full">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)} rows={3} placeholder="Optional notes..." />
            </div>
          </div>

          {/* Contract-Level Documents */}
          {!isNew && (
            <div className="scp-documents-section">
              <div className="scp-terms-header">
                <h3><FaPaperclip /> Contract Documents</h3>
                <DocumentUploadArea
                  uploadUrl={`/api/ServiceContracts/${contract.serviceContractId}/documents`}
                  apiFetch={apiFetch}
                  onUploaded={reloadContractData}
                />
              </div>
              {contractDocs.length === 0 ? (
                <div className="scp-terms-empty-msg">No documents attached. Upload MSAs, contracts, or other documents above.</div>
              ) : (
                <DocumentList documents={contractDocs} apiFetch={apiFetch} onDeleted={reloadContractData} />
              )}
            </div>
          )}

          {/* Terms Section */}
          <div className="scp-terms-section">
            <div className="scp-terms-header">
              <h3><FaLayerGroup /> Contract Terms</h3>
              <button className="scp-add-term-btn" onClick={addTerm}><FaPlus /> Add Term</button>
            </div>
            {terms.length === 0 ? (
              <div className="scp-terms-empty-msg">No terms added yet. Click "Add Term" to create one.</div>
            ) : (
              terms.map((term, idx) => {
                let termCustom = {};
                if (term.customData) {
                  try { termCustom = JSON.parse(term.customData); }
                  catch (catchError) {
                      Sentry.captureException(catchError);
                      /* ignore */
                  }
                }
                return (
                  <div key={idx} className="scp-term-card">
                    <div className="scp-term-card-header">
                      <span className="scp-term-num">Term {idx + 1}</span>
                      <button className="scp-remove-term" onClick={() => removeTerm(idx)} title="Remove term">
                        <FaTrash />
                      </button>
                    </div>
                    <div className="scp-form-grid">
                      <div className="scp-form-group">
                        <label>Name *</label>
                        <input type="text" value={term.name || ''} onChange={e => handleTermChange(idx, 'name', e.target.value)} placeholder="e.g. 20 Mbps Fibre" />
                      </div>
                      <div className="scp-form-group">
                        <label>Description</label>
                        <input type="text" value={term.description || ''} onChange={e => handleTermChange(idx, 'description', e.target.value)} placeholder="Optional description" />
                      </div>
                      <div className="scp-form-group">
                        <label>Start Date</label>
                        <input type="date" value={term.startDate ? term.startDate.substring(0, 10) : ''} onChange={e => handleTermChange(idx, 'startDate', e.target.value)} />
                      </div>
                      <div className="scp-form-group">
                        <label>Expiry Date</label>
                        <input type="date" value={term.expiryDate ? term.expiryDate.substring(0, 10) : ''} onChange={e => handleTermChange(idx, 'expiryDate', e.target.value)} />
                      </div>
                      <div className="scp-form-group">
                        <label>Reminder Days</label>
                        <input type="number" value={term.expiryReminderDays || ''} onChange={e => handleTermChange(idx, 'expiryReminderDays', e.target.value)} placeholder="e.g. 30" />
                      </div>
                      <div className="scp-form-group">
                        <label>Monthly Cost (R)</label>
                        <input type="number" step="0.01" value={term.monthlyCost || ''} onChange={e => handleTermChange(idx, 'monthlyCost', e.target.value)} placeholder="0.00" />
                      </div>
                      {/* Custom fields based on contract type */}
                      {customFields.map(f => (
                        <div key={f.key} className="scp-form-group">
                          <label>{f.label}{f.unit ? ` (${f.unit})` : ''}</label>
                          <input
                            type={f.type === 'number' || f.type === 'percentage' ? 'number' : 'text'}
                            step={f.type === 'percentage' ? '0.01' : undefined}
                            value={termCustom[f.key] ?? ''}
                            onChange={e => handleTermCustomDataChange(idx, f.key, e.target.value === '' ? null : (f.type === 'number' || f.type === 'percentage' ? Number(e.target.value) : e.target.value))}
                            placeholder={f.label}
                          />
                        </div>
                      ))}
                      <div className="scp-form-group scp-form-full">
                        <label>Term Notes</label>
                        <textarea value={term.notes || ''} onChange={e => handleTermChange(idx, 'notes', e.target.value)} rows={2} placeholder="Optional notes..." />
                      </div>
                      {/* Term-level documents (only for saved terms) */}
                      {!isNew && term.serviceContractTermId && (
                        <div className="scp-form-group scp-form-full scp-term-docs-section">
                          <label><FaPaperclip style={{ marginRight: 4 }} /> Term Documents</label>
                          <DocumentList
                            documents={term.documents || []}
                            apiFetch={apiFetch}
                            onDeleted={reloadContractData}
                          />
                          <DocumentUploadArea
                            uploadUrl={`/api/ServiceContracts/${contract.serviceContractId}/terms/${term.serviceContractTermId}/documents`}
                            apiFetch={apiFetch}
                            onUploaded={reloadContractData}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="scp-modal-footer">
          <button className="scp-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="scp-btn-save" onClick={handleSave} disabled={saving}>
            <FaSave /> {saving ? 'Saving…' : (isNew ? 'Create Contract' : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { key: 'all',     label: 'All' },
  { key: 'active',  label: 'Active' },
  { key: 'soon',    label: 'Expiring Soon' },
  { key: 'expired', label: 'Expired' },
  { key: 'unknown', label: 'No Expiry' }
];

const ServiceContractsPage = () => {
  const { apiFetch } = useAuth();

  const [contracts, setContracts] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortField, setSortField] = useState('expiry');
  const [sortDir, setSortDir] = useState('asc');

  // Row state
  const [expandedTermsId, setExpandedTermsId] = useState(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [editContract, setEditContract] = useState(undefined); // undefined=closed, null=new, object=edit

  // ── Load data ─────────────────────────────────────────────────────────────────

  const loadContracts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/ServiceContracts');
      if (!res.ok) throw new Error('Failed to load contracts');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load contracts');
      setContracts(json.data || []);
    } catch (err) {
        Sentry.captureException(err);
      toast.error(`Failed to load contracts: ${err.message}`);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const loadLookups = useCallback(async () => {
    try {
      const [typesRes, providersRes, deptsRes] = await Promise.all([
        apiFetch('/api/ServiceContracts/types'),
        apiFetch('/api/ServiceProviders?activeOnly=true'),
        apiFetch('/api/ServiceContracts/departments')
      ]);
      const typesJson = await typesRes.json();
      const providersJson = await providersRes.json();
      const deptsJson = await deptsRes.json();
      setContractTypes(typesJson.data || []);
      setProviders(providersJson.data || []);
      setDepartments(deptsJson.data || []);
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        /* lookups are best-effort */
    }
  }, [apiFetch]);

  useEffect(() => { loadContracts(); loadLookups(); }, [loadContracts, loadLookups]);

  // ── Derived data ──────────────────────────────────────────────────────────────

  const contractsWithStatus = contracts.map(c => ({
    ...c,
    _status: getContractStatus(c.earliestExpiry)
  }));

  const stats = {
    total:   contractsWithStatus.length,
    active:  contractsWithStatus.filter(c => c._status === 'active').length,
    expired: contractsWithStatus.filter(c => c._status === 'expired').length,
    soon:    contractsWithStatus.filter(c => c._status === 'soon').length
  };

  const categories = useMemo(() => {
    const cats = new Set(contractsWithStatus.map(c => c.contractTypeCategory).filter(Boolean));
    return Array.from(cats).sort();
  }, [contractsWithStatus]);

  const typeNames = useMemo(() => {
    const names = new Set(contractsWithStatus.map(c => c.contractTypeName).filter(Boolean));
    return Array.from(names).sort();
  }, [contractsWithStatus]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return contractsWithStatus
      .filter(c => {
        const matchesSearch = !q || (
          (c.serviceProviderName || '').toLowerCase().includes(q) ||
          (c.contractTypeName || '').toLowerCase().includes(q) ||
          (c.contractReference || '').toLowerCase().includes(q) ||
          (c.responsibleDepartmentName || '').toLowerCase().includes(q) ||
          (c.notes || '').toLowerCase().includes(q)
        );
        const matchesStatus = statusFilter === 'all' || c._status === statusFilter;
        const matchesCategory = categoryFilter === 'all' || c.contractTypeCategory === categoryFilter;
        const matchesType = typeFilter === 'all' || c.contractTypeName === typeFilter;
        return matchesSearch && matchesStatus && matchesCategory && matchesType;
      })
      .sort((a, b) => {
        let valA, valB;
        if (sortField === 'provider') {
          valA = (a.serviceProviderName || '').toLowerCase();
          valB = (b.serviceProviderName || '').toLowerCase();
        } else if (sortField === 'type') {
          valA = (a.contractTypeName || '').toLowerCase();
          valB = (b.contractTypeName || '').toLowerCase();
        } else if (sortField === 'cost') {
          valA = a.totalMonthlyCost ?? 0;
          valB = b.totalMonthlyCost ?? 0;
        } else {
          valA = a.earliestExpiry ? new Date(a.earliestExpiry).getTime() : Infinity;
          valB = b.earliestExpiry ? new Date(b.earliestExpiry).getTime() : Infinity;
        }
        if (valA < valB) return sortDir === 'asc' ? -1 : 1;
        if (valA > valB) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
  }, [contractsWithStatus, searchQuery, statusFilter, categoryFilter, typeFilter, sortField, sortDir]);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const handleSort = (field) => {
    if (sortField === field) { setSortDir(prev => prev === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <FaSortAmountDown style={{ marginLeft: 4, opacity: 0.7, fontSize: 10 }} />
      : <FaSortAmountUp   style={{ marginLeft: 4, opacity: 0.7, fontSize: 10 }} />;
  };

  const handleStatCardClick = (key) => setStatusFilter(prev => prev === key ? 'all' : key);

  const handleModalSaved = () => {
    setEditContract(undefined);
    loadContracts();
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm('Are you sure you want to deactivate this contract?')) return;
    try {
      const res = await apiFetch(`/api/ServiceContracts/${contractId}`, { method: 'DELETE' });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      toast.success('Contract deactivated.');
      loadContracts();
    } catch (err) {
        Sentry.captureException(err);
      toast.error(err.message);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="service-contracts-page">
      <div className="scp-page-header">
        <div className="scp-header-left">
          <h1><FaFileContract /> Service Provider Contracts</h1>
          <p className="scp-header-desc">
            Manage all service contracts — ISP, security, maintenance, and more — across your organisation.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="scp-add-btn"
            onClick={() => setEditContract(null)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, border: 'none', background: '#4caf50', cursor: 'pointer', fontSize: '0.88rem', color: '#fff', fontWeight: 600 }}
          >
            <FaPlus /> New Contract
          </button>
          <button
            className="scp-refresh-btn"
            onClick={loadContracts}
            title="Refresh contracts"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: '1px solid #ddd', background: '#f9f9f9', cursor: 'pointer', fontSize: '0.88rem', color: '#555' }}
          >
            <FaSync /> Refresh
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="scp-stats-bar">
        {[
          { key: 'all', label: 'Total Contracts', value: stats.total, colorClass: 'color-total' },
          { key: 'active', label: 'Active', value: stats.active, colorClass: 'color-active' },
          { key: 'soon', label: 'Expiring Soon', value: stats.soon, colorClass: 'color-soon' },
          { key: 'expired', label: 'Expired', value: stats.expired, colorClass: 'color-expired' },
        ].map(s => (
          <div key={s.key}
            className={`scp-stat-card${statusFilter === s.key ? ' active-filter' : ''}`}
            onClick={() => handleStatCardClick(s.key)}>
            <span className={`scp-stat-value ${s.colorClass}`}>{s.value}</span>
            <span className="scp-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="scp-toolbar">
        <div className="scp-search-wrap">
          <FaSearch className="scp-search-icon" />
          <input className="scp-search" type="text"
            placeholder="Search by provider, type, reference, department…"
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>

        {categories.length > 1 && (
          <div className="scp-category-filter" title="Filter by service category">
            <FaFilter style={{ fontSize: 12, color: '#888' }} />
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
              <option value="all">All Categories</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
        )}

        {typeNames.length > 1 && (
          <div className="scp-category-filter" title="Filter by contract type">
            <FaLayerGroup style={{ fontSize: 12, color: '#888' }} />
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="all">All Types</option>
              {typeNames.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}

        <div className="scp-filter-group">
          {STATUS_FILTERS.map(f => {
            const isActive = statusFilter === f.key;
            const activeCls = f.key === 'expired' ? 'active-expired' : f.key === 'soon' ? 'active-soon' : 'active';
            return (
              <button key={f.key} className={`scp-filter-btn${isActive ? ` ${activeCls}` : ''}`}
                onClick={() => setStatusFilter(f.key)}>{f.label}</button>
            );
          })}
        </div>

        <span className="scp-result-count">{filtered.length} of {contracts.length} contracts</span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="scp-loading"><FaSync style={{ animation: 'spin 1s linear infinite' }} /> Loading contracts…</div>
      ) : filtered.length === 0 ? (
        <div className="scp-empty">
          {contracts.length === 0 ? 'No service contracts have been created yet.' : 'No contracts match your current filters.'}
        </div>
      ) : (
        <div className="scp-table-wrap">
          <table className="scp-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th className="scp-th-sortable" onClick={() => handleSort('provider')}>
                  Service Provider <SortIcon field="provider" />
                </th>
                <th className="scp-th-sortable" onClick={() => handleSort('type')}>
                  Contract Type <SortIcon field="type" />
                </th>
                <th>Reference</th>
                <th>Department</th>
                <th>Terms</th>
                <th className="scp-th-sortable" onClick={() => handleSort('cost')}>
                  Monthly Cost <SortIcon field="cost" />
                </th>
                <th className="scp-th-sortable" onClick={() => handleSort('expiry')}>
                  Earliest Expiry <SortIcon field="expiry" />
                </th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(contract => {
                const isTermsOpen = expandedTermsId === contract.serviceContractId;
                const isHistoryOpen = expandedHistoryId === contract.serviceContractId;
                return (
                  <React.Fragment key={contract.serviceContractId}>
                    <tr className="scp-row">
                      {/* Expand toggle */}
                      <td style={{ cursor: 'pointer', textAlign: 'center' }}
                          onClick={() => setExpandedTermsId(prev => prev === contract.serviceContractId ? null : contract.serviceContractId)}>
                        {contract.termCount > 0 && (
                          isTermsOpen ? <FaChevronDown style={{ color: '#4caf50', fontSize: 11 }} /> : <FaChevronRight style={{ color: '#999', fontSize: 11 }} />
                        )}
                      </td>

                      {/* Provider */}
                      <td>
                        <div className="scp-provider-name">{contract.serviceProviderName || '—'}</div>
                        {contract.serviceProviderCategory && (
                          <div className="scp-provider-category">{contract.serviceProviderCategory}</div>
                        )}
                      </td>

                      {/* Contract Type */}
                      <td>
                        <ContractTypeBadge
                          name={contract.contractTypeName}
                          iconClass={contract.contractTypeIconClass}
                          color={contract.contractTypeColor} />
                        {contract.contractTypeSubCategory && (
                          <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 2 }}>{contract.contractTypeSubCategory}</div>
                        )}
                      </td>

                      {/* Reference */}
                      <td style={{ fontSize: '0.85rem' }}>{contract.contractReference || '—'}</td>

                      {/* Department */}
                      <td style={{ fontSize: '0.85rem' }}>{contract.responsibleDepartmentName || '—'}</td>

                      {/* Terms count */}
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ background: '#f0f0f0', borderRadius: 10, padding: '2px 8px', fontSize: '0.8rem', fontWeight: 600 }}>
                          {contract.termCount}
                        </span>
                      </td>

                      {/* Monthly cost */}
                      <td className="scp-speed-cell">{formatCurrency(contract.totalMonthlyCost)}</td>

                      {/* Earliest expiry */}
                      <td><ExpiryCell expiryDate={contract.earliestExpiry} /></td>

                      {/* Status */}
                      <td><StatusBadge status={contract._status} /></td>

                      {/* Actions */}
                      <td>
                        <div className="scp-actions">
                          <button className="btn-edit" onClick={() => setEditContract(contract)} title="Edit contract">
                            <FaEdit /> Edit
                          </button>
                          <button className={`btn-history${isHistoryOpen ? ' open' : ''}`}
                            onClick={() => setExpandedHistoryId(prev => prev === contract.serviceContractId ? null : contract.serviceContractId)}
                            title={isHistoryOpen ? 'Hide history' : 'View change history'}>
                            <FaHistory />
                            {isHistoryOpen ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                          <button className="btn-delete" onClick={() => handleDelete(contract.serviceContractId)} title="Deactivate contract">
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Terms expansion */}
                    {isTermsOpen && contract.terms && (
                      <tr className="scp-history-row">
                        <td colSpan={10}>
                          <TermsPanel terms={contract.terms} customFieldsSchema={contract.customFieldsSchema} />
                        </td>
                      </tr>
                    )}

                    {/* History expansion */}
                    {isHistoryOpen && (
                      <tr className="scp-history-row">
                        <td colSpan={10}>
                          <HistoryPanel contractId={contract.serviceContractId} apiFetch={apiFetch} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit / Create Modal */}
      {editContract !== undefined && (
        <ContractEditModal
          contract={editContract}
          contractTypes={contractTypes}
          providers={providers}
          departments={departments}
          apiFetch={apiFetch}
          onClose={() => setEditContract(undefined)}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
};

export default ServiceContractsPage;
